import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";

import Logo from "~/components/Logo";
import { PageTransition } from "~/components/PageTransition";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { authClient } from "~/lib/auth";
import { exchangeImpersonationCode } from "~/lib/impersonation";

export const Route = createFileRoute("/impersonate")({
  validateSearch: z.object({
    code: z.string().min(1).optional(),
  }),
  component: ImpersonateRoute,
});

function ImpersonateRoute() {
  const { code } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;

    let cancelled = false;
    void (async () => {
      try {
        await exchangeImpersonationCode(code);
        if (cancelled) return;
        await authClient.getSession();
        await navigate({ to: "/dashboard", replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Er ging iets mis.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  const displayError = !code ? "Deze support-link is ongeldig of verlopen." : error;

  return (
    <div className="bg-background relative flex min-h-screen w-full items-center justify-center px-4">
      <div className="flex w-full max-w-md flex-col gap-6">
        <PageTransition className="w-full" delay={0}>
          <div className="flex flex-col gap-4">
            <Logo className="-ml-3 lg:size-14" />
            <h1 className="text-2xl font-medium lg:text-3xl">
              {displayError ? "Link ongeldig" : "Bezig met openen…"}
            </h1>
            {displayError ? (
              <Alert variant="destructive">
                <AlertDescription>{displayError}</AlertDescription>
              </Alert>
            ) : (
              <p className="text-muted-foreground flex items-center gap-2 text-sm">
                <LoaderCircle className="size-4 shrink-0 animate-spin" aria-hidden />
                Even geduld, we openen de klantweergave.
              </p>
            )}
          </div>
        </PageTransition>
      </div>
      <img
        src="/corner-pattern.svg"
        className="invisible absolute bottom-0 left-0 opacity-50 lg:visible"
        alt=""
      />
    </div>
  );
}
